import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import BrandLogo from './BrandLogo';
import { authService } from '../api/api';

function resetTokenFromHash() {
  const hash = window.location.hash || '';
  const queryStart = hash.indexOf('?');
  if (queryStart < 0) return '';
  return new URLSearchParams(hash.slice(queryStart + 1)).get('token') || '';
}

export default function ResetPasswordPage() {
  const token = useMemo(() => resetTokenFromHash(), []);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    if (!token) {
      setError('Password reset link is missing or invalid.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setSaving(true);
      const res = await authService.resetPassword({ token, password });
      setMessage(res.data?.message || 'Password reset successfully. You can now log in.');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to reset password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 460, borderRadius: 4, boxShadow: '0 24px 80px rgba(0,31,63,0.28)' }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <BrandLogo height={{ xs: 132, sm: 160 }} showText={false} logoGlow sx={{ justifyContent: 'center', mb: 1.5 }} />
            <LockResetIcon sx={{ fontSize: 38, color: '#001f3f', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 900, color: '#001f3f' }}>
              Reset Password
            </Typography>
            <Typography color="text.secondary" sx={{ fontWeight: 700 }}>
              Enter a new BioFleet password.
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {message && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={() => { window.location.hash = '/'; }}>Login</Button>}
            >
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={submit}>
            <TextField
              fullWidth
              required
              type="password"
              label="New Password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              helperText="Minimum 8 characters."
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              required
              type="password"
              label="Confirm Password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              sx={{ mb: 3 }}
            />
            <Button fullWidth type="submit" variant="contained" disabled={saving || Boolean(message)} sx={{ py: 1.25, borderRadius: 2, fontWeight: 900, bgcolor: '#001f3f' }}>
              {saving ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
            </Button>
            <Button fullWidth sx={{ mt: 1, fontWeight: 800 }} onClick={() => { window.location.hash = '/'; }}>
              Back to Login
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
